import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePostHog } from '@posthog/react';
import { STATUS_COLORS, STATUS_LABELS, getCompetitiveStyle, getFundingTypeStyle } from '../../utils/constants';
import { formatPaymentRates, getDeadlineDisplay } from '../../utils/helpers';
import SaveButton from './SaveButton';
import AlertButton from './AlertButton';
import Tooltip from '../common/Tooltip';
import AuthModal from '../auth/AuthModal';

const FundingCard = ({ funding, onUnsave, onCompatibilityFilter, onSaveToggle }) => {
    const { isAuthenticated } = useAuth();
    const posthog = usePostHog();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authAction, setAuthAction] = useState('');
    const detailUrl = funding?.slug ? `/funding/${funding.slug}` : (funding?.id ? `/funding/${funding.id}` : '#');

    const handleCardLinkClick = (e) => {
        if (!isAuthenticated) {
            e.preventDefault();
            setAuthAction('view this funding opportunity');
            setShowAuthModal(true);
        }
    };

    const handleSaveToggle = (fundingId, isSaved) => {
        // If the item is unsaved and we have an onUnsave callback, call it
        if (!isSaved && onUnsave) {
            onUnsave(fundingId);
        }
        if (onSaveToggle) onSaveToggle(fundingId, isSaved);
    };

    const formatCurrency = (funding) => formatPaymentRates(funding.payment_rates, { maxRates: 3 });

    const truncateDescription = (text, limit = 125) => {
        if (!text) return '';
        if (text.length <= limit) return text;
        return text.substring(0, limit).trim() + '...';
    };

    const parseArrayString = (data) => {
        if (!data) return [];

        let result = data;

        // Handle string data
        if (typeof result === 'string') {
            // Try to parse if it's a string representation of an array
            if (result.startsWith('[')) {
                try {
                    // Convert single quotes to double quotes for proper JSON parsing
                    const jsonString = result.replace(/'/g, '"');
                    result = JSON.parse(jsonString);
                } catch (e) {
                    // If JSON parsing fails, treat as single string
                    result = [result];
                }
            } else {
                // Single string value
                result = [result];
            }
        } else if (!Array.isArray(result)) {
            // Handle any other data type by converting to array
            result = [result];
        }

        // Filter out empty or null values
        return result.filter(item => item && item.toString().trim());
    };

    // Use the funding data directly (no sample data)
    const fundingData = funding || {};

    const status = fundingData.status?.toLowerCase();
    const isClosed = status === 'closed';
    const isHistoric = status === 'historic';
    const isMuted = isClosed || isHistoric;

    // Card-level derived styles based on status
    const headerBg = isMuted ? '#EBEBEB' : '#F6F5EC';
    const titleColor = isMuted ? '#888888' : '#222222';
    const valueColor = isMuted ? '#AAAAAA' : '#615DEC';
    const metaColor = isMuted ? '#BBBBBB' : '#7C7B7B';
    const funderBg = isMuted ? '#C0C0C0' : '#615DEC';
    const collectionBg = isMuted ? 'bg-gray-200' : 'bg-purple-100';
    const collectionText = isMuted ? 'text-gray-500' : 'text-purple-800';
    const farmTagBg = isMuted ? '#E0E0E0' : '#F6F5EC';
    const farmTagColor = isMuted ? '#999999' : '#222222';

    return (
        <div
            className={`group relative bg-white border-2 transition-all duration-300 ${isClosed ? 'border-gray-200' : 'border-gray-100 hover:border-gray-200 hover:shadow-xl'}`}
            style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
        >
            {/* Stretched link — covers entire card, gives native Cmd+click, middle-click, right-click */}
            <a
                href={detailUrl}
                onClick={handleCardLinkClick}
                className="absolute inset-0 z-0"
                aria-label={`View details for ${fundingData.name}`}
            />
            {/* Header Section */}
            <div className="px-6 pt-4 pb-4" style={{ backgroundColor: headerBg }}>
                {/* First Line: Status tags and Heart */}
                <div className="flex justify-between items-center mb-2">
                    {/* Status tag */}
                    <div className="flex items-center gap-2">
                        {status && STATUS_COLORS[status] && (
                            <span
                                className={`px-2 py-0.5 text-xs font-semibold rounded-full ${isClosed ? 'bg-red-100 text-red-700' : isHistoric ? 'bg-gray-200 text-gray-600' : 'text-white'}`}
                                style={isClosed || isHistoric ? {} : { backgroundColor: STATUS_COLORS[status] }}
                            >
                                {STATUS_LABELS[status] || status}
                            </span>
                        )}
                    </div>

                    {/* Right side: compatible schemes button + heart — elevated above the stretched link */}
                    <div className="relative z-10 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {onCompatibilityFilter && (
                            <Tooltip text="Add to stack">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        posthog.capture('compatible_schemes_searched', {
                                            funding_id: funding.id,
                                            funding_name: funding.name,
                                        });
                                        onCompatibilityFilter({ id: funding.id, name: funding.name, fund_collection: funding.fund_collection, payment_rates: funding.payment_rates });
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors"
                                >
                                    <svg className="h-5 w-5 translate-y-[1px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                                    </svg>
                                </button>
                            </Tooltip>
                        )}
                        <AlertButton schemeId={fundingData.id} />
                        <SaveButton
                            funding={fundingData}
                            onSaveToggle={handleSaveToggle}
                        />
                    </div>
                </div>

                {/* Second Line: Grant Name */}
                <h3 className="text-xl font-bold leading-tight mb-2 line-clamp-2" style={{ color: titleColor }}>
                    {fundingData.name || truncateDescription(fundingData.description, 60)}
                </h3>

                {/* Third Line: Value */}
                <div className="text-lg font-semibold mb-2" style={{ color: valueColor }}>
                    {formatCurrency(fundingData)}
                </div>

                {/* Funder & Fund Collection */}
                <div className="mb-3">
                    {fundingData.funders && fundingData.funders.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                            {fundingData.funders.map((funder, index) => (
                                <span
                                    key={`funder-${index}`}
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-white"
                                    style={{ backgroundColor: funderBg }}
                                >
                                    {funder}
                                </span>
                            ))}
                        </div>
                    )}

                    {fundingData.fund_collection && (
                        <div className="flex flex-wrap gap-2">
                            {parseArrayString(fundingData.fund_collection).map((collection, index) => (
                                <span
                                    key={`collection-${index}`}
                                    className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${collectionBg} ${collectionText}`}
                                >
                                    {collection}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Body Content */}
            <div className="p-6 pt-4" style={{ backgroundColor: '#ffffff' }}>
                {/* Description */}
                <p className="text-sm leading-relaxed mb-4 line-clamp-3" style={{ color: titleColor }}>
                    {truncateDescription(fundingData.description, 125)}
                </p>

                {/* Supplementary */}
                {fundingData.is_supplementary && (
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                        <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '3px 10px', borderRadius: 20,
                            fontSize: '0.7rem', fontWeight: 600,
                            color: '#615DEC', border: '1.5px solid #615DEC',
                            background: 'transparent',
                        }}>
                            Supplementary
                        </span>
                    </div>
                )}

                {/* Type Bubbles */}
                {(fundingData.competitive?.filter(Boolean).length > 0 || fundingData.funding_type) && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                        {fundingData.competitive?.filter(Boolean).map((c, i) => {
                            const cs = getCompetitiveStyle(c);
                            return (
                                <span key={i} style={{
                                    display: 'inline-flex', alignItems: 'center',
                                    padding: '3px 10px', borderRadius: 20,
                                    fontSize: '0.7rem', fontWeight: 600,
                                    background: cs.bg, color: cs.color,
                                }}>
                                    {cs.label}
                                </span>
                            );
                        })}
                        {fundingData.funding_type && (() => {
                            const fts = getFundingTypeStyle(fundingData.funding_type);
                            return (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center',
                                    padding: '3px 10px', borderRadius: 20,
                                    fontSize: '0.7rem', fontWeight: 600,
                                    background: fts.bg, color: fts.color,
                                }}>
                                    {fts.label}
                                </span>
                            );
                        })()}
                    </div>
                )}

                {/* Key Details */}
                <div className="space-y-2 mb-4">
                    {fundingData.duration && (
                        <div className="flex items-center text-sm" style={{ color: metaColor }}>
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">Duration:</span>
                            <span className="ml-2">{fundingData.duration}</span>
                        </div>
                    )}

                    {/* Deadline — hidden for closed schemes */}
                    {!isClosed && (fundingData.deadline_date || fundingData.deadline_tag) && (
                        <div className="flex items-center text-sm" style={{ color: metaColor }}>
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="font-medium">Deadline:</span>
                            <span className="ml-2">{getDeadlineDisplay(fundingData)}</span>
                        </div>
                    )}


                </div>

                {/* Farm Types */}
                {fundingData.farm_types?.filter(t => t && t.toLowerCase() !== 'not specified').length > 0 && (
                    <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                            {fundingData.farm_types.filter(t => t && t.toLowerCase() !== 'not specified').slice(0, 3).map((type, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 text-xs font-semibold rounded-full"
                                    style={{ backgroundColor: farmTagBg, color: farmTagColor }}
                                >
                                    {type}
                                </span>
                            ))}
                            {fundingData.farm_types.length > 3 && (
                                <span
                                    className="px-3 py-1 text-xs font-semibold rounded-full"
                                    style={{ backgroundColor: isClosed ? '#C8C8C8' : '#7C7B7B', color: '#ffffff' }}
                                >
                                    +{fundingData.farm_types.length - 3} more
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Action Footer */}
            <div className="px-6 py-4 border-t-2 border-gray-100">
                <div className="flex items-center justify-between">
                    <span
                        className="text-sm font-semibold transition-all"
                        style={{ color: isClosed ? '#AAAAAA' : '#615DEC' }}
                    >
                        {isClosed ? 'View details →' : 'Click to view details →'}
                    </span>
                </div>
            </div>

            {/* AuthModal for unauthenticated users */}
            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                action={authAction}
                fundingId={funding.slug || funding.id}
            />
        </div>
    );
};

export default FundingCard;